import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  IconButton,
  MenuItem,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { IconPlus, IconTrashX, IconArrowDownCircle } from "@tabler/icons-react";
import { Moment } from "moment";
import moment from "moment-timezone";
import { useUsuario } from "../../../../providers/usuarioProvider";
import Functions from "../../../../callable/functions";
import PosicaoInternacional from "../../../../models/posicaoInternacional";
import PosicaoAtivoInternacional from "../../../../models/posicaoAtivoInternacional";
import { MoedaInternacional } from "../../../../models/enum";
import MoedaInput from "../../../util/ui/inputs/moedaInput";
import InteiroInput from "../../../util/ui/inputs/inteiroInput";
import { LoadingButton } from "@mui/lab";
import { onSnapshot } from "firebase/firestore";
import PosicaoInternacionalDb from "../../../../db/posicaoInternacionalDb";
import { felixTokens, radius } from "../../../../theme";
import { avaliarCambioImplicito } from "./cambioImplicito";

type Ptax = Partial<
  Record<
    MoedaInternacional,
    { compra: number; venda: number; dataCotacao: string }
  >
>;

const formatarCambio = (valor: number) =>
  valor.toLocaleString("pt-BR", {
    minimumFractionDigits: 4,
    maximumFractionDigits: 4,
  });

/** Ticker e código são dado, não texto: mono dentro do campo. */
const campoDado = {
  "& input": {
    fontFamily: felixTokens.fontMono,
    fontVariantNumeric: "tabular-nums" as const,
    textTransform: "uppercase" as const,
  },
};

/** Select de moeda: rótulo mono tracked, igual à coluna onde ela aparece. */
const campoMoeda = {
  "& .MuiSelect-select": {
    fontFamily: felixTokens.fontMono,
    letterSpacing: "0.08em",
  },
};

type Props = {
  data: Moment;
};

interface LinhaAtivo {
  codigoAtivo: string;
  moeda: MoedaInternacional;
  quantidade: number;
  custoMedioOriginal: number;
  custoMedioBRL: number;
}

const linhaVazia: LinhaAtivo = {
  codigoAtivo: "",
  moeda: MoedaInternacional.USD,
  quantidade: 0,
  custoMedioOriginal: 0,
  custoMedioBRL: 0,
};

const moedas = Object.values(MoedaInternacional);

export default function TabelaCustodiaInternacional({ data }: Props) {
  const { visualizado } = useUsuario();
  const [linhas, setLinhas] = useState<LinhaAtivo[]>([{ ...linhaVazia }]);
  const [salvando, setSalvando] = useState(false);
  // PTAX amarrado à consulta que o produziu (data + moedas): ao trocar a data
  // o aviso não usa a cotação da data anterior enquanto a nova não chega.
  const [ptaxConsulta, setPtaxConsulta] = useState<{
    chave: string;
    valores: Ptax;
  }>();
  const [ptaxErro, setPtaxErro] = useState(false);
  const [tentativa, setTentativa] = useState(0);

  // PTAX da data do ponto de partida para as moedas em uso: referência do
  // aviso de câmbio implícito. Sem a cotação (falha de rede), a coluna mostra
  // só o câmbio implícito, sem aviso.
  const moedasEmUso = useMemo(
    () =>
      [...new Set(linhas.filter((l) => l.codigoAtivo).map((l) => l.moeda))]
        .sort()
        .join(","),
    [linhas]
  );
  // Dependência pela data formatada, não pelo objeto Moment: um Moment novo a
  // cada render do pai dispararia uma consulta de câmbio por render.
  const dataISO = data.clone().tz("America/Sao_Paulo").format("YYYY-MM-DD");
  const chavePtax = `${dataISO}|${moedasEmUso}`;
  useEffect(() => {
    if (!visualizado || !moedasEmUso) return;
    let ativo = true;
    setPtaxErro(false);
    new Functions()
      .obterCambioDoDia({
        data: dataISO,
        moedas: moedasEmUso.split(",") as MoedaInternacional[],
      })
      .then(
        (r) =>
          ativo && setPtaxConsulta({ chave: chavePtax, valores: r.data ?? {} })
      )
      .catch(() => ativo && setPtaxErro(true));
    return () => {
      ativo = false;
    };
  }, [dataISO, visualizado, moedasEmUso, chavePtax, tentativa]);
  const ptax: Ptax =
    ptaxConsulta?.chave === chavePtax ? ptaxConsulta.valores : {};

  const situacoes = linhas.map((l) =>
    avaliarCambioImplicito(
      l.custoMedioOriginal,
      l.custoMedioBRL,
      ptax[l.moeda]?.compra
    )
  );
  const linhasForaDeEscala = linhas.filter(
    (l, i) => l.codigoAtivo && l.quantidade > 0 && situacoes[i].tipo !== "ok"
  );

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        new PosicaoInternacionalDb().doc({
          usuarioId: visualizado,
          posicaoId: data.format("YYYY-MM-DD"),
        }),
        (snapshot) => {
          if (snapshot.exists()) {
            const posicao = snapshot.data()!;
            if (posicao.posicoes.length > 0) {
              setLinhas(
                posicao.posicoes.map((p) => ({
                  codigoAtivo: p.codigoAtivo,
                  moeda: p.moeda,
                  quantidade: p.quantidade,
                  custoMedioOriginal: p.custoMedioOriginal,
                  custoMedioBRL: p.custoMedioBRL,
                }))
              );
            }
          }
        }
      );
  }, [data, visualizado]);

  const adicionarLinha = () => {
    setLinhas([...linhas, { ...linhaVazia }]);
  };

  const removerLinha = (index: number) => {
    setLinhas(linhas.filter((_, i) => i !== index));
  };

  const atualizarLinha = (
    index: number,
    campo: keyof LinhaAtivo,
    valor: any
  ) => {
    setLinhas(
      linhas.map((linha, i) =>
        i === index ? { ...linha, [campo]: valor } : linha
      )
    );
  };

  const calcularValorTotal = (linha: LinhaAtivo) => {
    return linha.quantidade * linha.custoMedioOriginal;
  };

  const calcularValorTotalBRL = (linha: LinhaAtivo) => {
    return linha.quantidade * linha.custoMedioBRL;
  };

  const salvar = async () => {
    setSalvando(true);

    const posicoes = linhas
      .filter((l) => l.codigoAtivo && l.quantidade > 0)
      .map(
        (l) =>
          new PosicaoAtivoInternacional({
            codigoAtivo: l.codigoAtivo.toUpperCase(),
            moeda: l.moeda,
            quantidade: l.quantidade,
            custoMedioOriginal: l.custoMedioOriginal,
            custoMedioBRL: l.custoMedioBRL,
            valorTotalOriginal: calcularValorTotal(l),
            valorTotalBRL: calcularValorTotalBRL(l),
          })
      );

    return new Functions()
      .posicaoInicialInternacional({
        posicaoInicial: new PosicaoInternacional({
          data: data.clone(),
          posicoes: posicoes,
        }).toJSON(),
      })
      .finally(() => setSalvando(false));
  };

  return (
    <Container fixed disableGutters sx={{ py: 2 }}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        paddingBottom={2}
      >
        <Typography variant="h3" color="text.primary">
          Custodia inicial em{" "}
          <Typography
            component="span"
            variant="dataMono"
            sx={{ fontSize: "inherit", fontWeight: 500 }}
          >
            {data.clone().tz("America/Sao_Paulo").format("DD/MM/YYYY")}
          </Typography>
        </Typography>
        <Box paddingX={1} />
        <LoadingButton
          variant="contained"
          size="small"
          color="success"
          onClick={salvar}
          loadingPosition="start"
          startIcon={<IconArrowDownCircle />}
          loading={salvando}
        >
          Salvar
        </LoadingButton>
      </Box>

      {linhasForaDeEscala.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {linhasForaDeEscala.length === 1
            ? `A linha de ${linhasForaDeEscala[0].codigoAtivo.toUpperCase()} está`
            : `${linhasForaDeEscala.length} linhas estão`}{" "}
          com custo médio faltando ou com o câmbio implícito muito longe do PTAX
          da data. Pode ser um custo antigo legítimo, mas confira se o custo
          médio em reais acompanhou a quantidade e o custo na moeda (por
          exemplo, depois de ajustar um desdobramento).
        </Alert>
      )}

      {ptaxErro && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={() => setTentativa((t) => t + 1)}
            >
              Tentar de novo
            </Button>
          }
        >
          Não foi possível obter o PTAX da data, então a conferência do câmbio
          implícito está desligada.
        </Alert>
      )}

      {/* Painel de tabela: hairline + radius-xl, sem sombra (DESIGN.md → Elevation). */}
      <TableContainer
        sx={{
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: `${radius.xl}px`,
          "& tbody tr:last-of-type td": { borderBottom: "none" },
        }}
      >
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Código</TableCell>
              <TableCell>Moeda</TableCell>
              <TableCell align="right">Quantidade</TableCell>
              <TableCell align="right">Custo médio (moeda)</TableCell>
              <TableCell align="right">Custo médio (BRL)</TableCell>
              <TableCell align="right">Câmbio implícito</TableCell>
              <TableCell align="right">Valor total (moeda)</TableCell>
              <TableCell align="right">Valor total (BRL)</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {linhas.map((linha, index) => (
              <TableRow key={index}>
                <TableCell>
                  <TextField
                    size="small"
                    value={linha.codigoAtivo}
                    onChange={(e) =>
                      atualizarLinha(index, "codigoAtivo", e.target.value)
                    }
                    placeholder="AAPL"
                    sx={{ width: 100, ...campoDado }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    select
                    size="small"
                    value={linha.moeda}
                    onChange={(e) =>
                      atualizarLinha(
                        index,
                        "moeda",
                        e.target.value as MoedaInternacional
                      )
                    }
                    sx={{ width: 90, ...campoMoeda }}
                  >
                    {moedas.map((moeda) => (
                      <MenuItem key={moeda} value={moeda}>
                        <Typography
                          variant="labelMono"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {moeda}
                        </Typography>
                      </MenuItem>
                    ))}
                  </TextField>
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={linha.quantidade}
                    onChange={(e) => {
                      const floatValue: number = (e.target as any).floatValue;
                      atualizarLinha(index, "quantidade", floatValue || 0);
                    }}
                    InputProps={{
                      inputComponent: InteiroInput,
                      // Cotas fracionadas (1,6 depois de um grupamento, 0,25 de
                      // compra fracionada): até 6 casas.
                      inputProps: { decimalScale: 6 },
                    }}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={linha.custoMedioOriginal}
                    onChange={(e) => {
                      const floatValue: number = (e.target as any).floatValue;
                      atualizarLinha(
                        index,
                        "custoMedioOriginal",
                        floatValue || 0
                      );
                    }}
                    InputProps={{
                      inputComponent: MoedaInput,
                      inputProps: { prefix: `${linha.moeda} ` },
                    }}
                    sx={{ width: 130 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    value={linha.custoMedioBRL}
                    onChange={(e) => {
                      const floatValue: number = (e.target as any).floatValue;
                      atualizarLinha(index, "custoMedioBRL", floatValue || 0);
                    }}
                    InputProps={{
                      inputComponent: MoedaInput,
                    }}
                    sx={{ width: 130 }}
                  />
                </TableCell>
                <TableCell align="right">
                  {(() => {
                    const situacao = situacoes[index];
                    const ref = ptax[linha.moeda];
                    const linhaPreenchida =
                      !!linha.codigoAtivo && linha.quantidade > 0;
                    if (situacao.tipo === "sem-custos" && !linhaPreenchida) {
                      return (
                        <Typography variant="dataMono" color="text.secondary">
                          —
                        </Typography>
                      );
                    }
                    if (situacao.tipo !== "ok" && situacao.tipo !== "fora") {
                      return (
                        <Chip
                          label={
                            situacao.tipo === "sem-brl"
                              ? "sem custo em R$"
                              : situacao.tipo === "sem-moeda"
                              ? `sem custo em ${linha.moeda}`
                              : "sem custo"
                          }
                          size="small"
                          sx={(theme) => ({
                            backgroundColor: theme.palette.statusChip.warn.bg,
                            color: theme.palette.statusChip.warn.fg,
                          })}
                        />
                      );
                    }
                    const valor = (
                      <Typography variant="dataMono">
                        {formatarCambio(situacao.implicito)}
                      </Typography>
                    );
                    if (situacao.tipo === "ok") return valor;
                    return (
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="flex-end"
                        gap={0.75}
                      >
                        {valor}
                        <Tooltip
                          describeChild
                          title={`PTAX de ${linha.moeda} em ${
                            ref
                              ? moment(ref.dataCotacao).format("DD/MM/YYYY")
                              : "—"
                          }: ${formatarCambio(
                            situacao.ptax
                          )}. O câmbio implícito desta linha está muito longe dele: pode ser um custo antigo legítimo, mas confira se o custo em reais corresponde ao custo na moeda.`}
                        >
                          <Chip
                            label="verificar"
                            size="small"
                            tabIndex={0}
                            sx={(theme) => ({
                              backgroundColor: theme.palette.statusChip.warn.bg,
                              color: theme.palette.statusChip.warn.fg,
                              // O Chip do MUI zera o outline: foco visível explícito.
                              "&:focus-visible": {
                                outline: `2px solid ${theme.palette.primary.main}`,
                                outlineOffset: 2,
                              },
                            })}
                          />
                        </Tooltip>
                      </Box>
                    );
                  })()}
                </TableCell>
                <TableCell align="right">
                  {/* Moeda é rótulo, valor é dado: mono tabular à direita. */}
                  <Typography
                    component="span"
                    variant="labelMono"
                    color="text.secondary"
                    sx={{ mr: 0.75 }}
                  >
                    {linha.moeda}
                  </Typography>
                  <Typography component="span" variant="dataMono">
                    {calcularValorTotal(linha).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography
                    component="span"
                    variant="labelMono"
                    color="text.secondary"
                    sx={{ mr: 0.75 }}
                  >
                    R$
                  </Typography>
                  <Typography
                    component="span"
                    variant="dataMono"
                    sx={{ fontWeight: 500 }}
                  >
                    {calcularValorTotalBRL(linha).toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </Typography>
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => removerLinha(index)}
                    disabled={linhas.length === 1}
                  >
                    <IconTrashX size={18} />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box paddingTop={2}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<IconPlus size={18} />}
          onClick={adicionarLinha}
        >
          Adicionar ativo
        </Button>
      </Box>
    </Container>
  );
}
